import { useEffect, useState } from 'react';

import { checkInboundOrder } from 'services/unit-orders';

type Check = {
  key: string;
  status: 'pending' | 'allowed' | 'blocked' | 'unavailable';
  message: string | null;
};

export const useInboundOrderCheck = (unitNumber: string, customerId?: number, enabled = true) => {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<Check | null>(null);
  const required = enabled && !!unitNumber && !!customerId;
  const key = JSON.stringify([unitNumber, customerId, required, attempt]);

  useEffect(() => {
    if (!required || !customerId) return;
    let current = true;
    const controller = new AbortController();
    const unavailable = () =>
      setResult({
        key,
        status: 'unavailable',
        message: 'Unable to verify stop / allow orders. Retry before continuing.'
      });
    const timeout = setTimeout(() => {
      if (!current) return;
      current = false;
      controller.abort();
      unavailable();
    }, 10000);

    checkInboundOrder(unitNumber, customerId, controller.signal)
      .then(check => {
        if (current) setResult({ key, status: check.allowed ? 'allowed' : 'blocked', message: check.message });
      })
      .catch(() => {
        if (current) unavailable();
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      current = false;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [key, unitNumber, customerId, required]);

  // A new identity is pending in this render, before its effect or network request starts.
  const check: Check = !required
    ? { key, status: 'allowed', message: null }
    : result?.key === key
    ? result
    : { key, status: 'pending', message: 'Checking stop / allow orders...' };
  return { ...check, retry: () => setAttempt(n => n + 1) };
};
